import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Checkbox,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useSnackbar } from 'notistack';
// components
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import FormProvider, { RequiredLabel, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import { DatePicker } from '@mui/x-date-pickers';
import { createCampaign, updateCampaign } from 'src/api/campaign';
import { convertErrorsToArray } from 'src/utils/flatten-array';

// ----------------------------------------------------------------------

export default function CreateCampaignModal({ isEdit, item, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const campaign_types = [
    { value: 'Scan', label: 'Scan' },
    { value: 'Wrap', label: 'Wrap' },
  ];

  // Validation schema
  const NewUserSchema = Yup.object().shape({
    campaign_name: Yup.string().required('Campaign Name is required'),
    campaign_type: Yup.string().required('Campaign Type is required'),
    start_date: Yup.date().required('Start Date is required').typeError('Invalid date'),
    end_date: Yup.date()
      .required('End Date is required')
      .typeError('Invalid date')
      .min(Yup.ref('start_date'), 'End Date cannot be before Start Date'),
  });

  const defaultValues = useMemo(
    () => ({
      campaign_type: item?.type || '',
      campaign_name: item?.name || '',
      start_date: new Date(item?.startDate) || {},
      end_date: new Date(item?.endDate) || {},
    }),
    [item]
  );

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });
  const { reset, handleSubmit, control } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const onSubmit = handleSubmit(async (value) => {
    const createData = {
      name: value.campaign_name,
      type: String(value.campaign_type),
      startDate:
        value.start_date instanceof Date
          ? value.start_date.toISOString()
          : String(value.start_date),
      endDate:
        value.end_date instanceof Date ? value.end_date.toISOString() : String(value.end_date),
    };
    const updateData = {
      campaignId: item?.id || '',
      name: value.campaign_name,
      type: String(value.campaign_type),
      startDate:
        value.start_date instanceof Date
          ? value.start_date.toISOString()
          : String(value.start_date),
      endDate:
        value.end_date instanceof Date ? value.end_date.toISOString() : String(value.end_date),
      status: item?.status,
    };

    try {
      setLoading(true);
      const data = isEdit ? await updateCampaign(updateData) : await createCampaign(createData);
      if (data.code === 200) {
        onClose();
        enqueueSnackbar(data.message);
        setTimeout(() => {
          reset();
        }, 0);
      }
      setLoading(false);
    } catch (error) {
      const errorsArray = convertErrorsToArray(error.data);

      if (errorsArray.length > 0) {
        errorsArray.forEach((err) => enqueueSnackbar(err, { variant: 'error' }));
      } else if (error.message) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Internal Server Error', { variant: 'error' });
      }
      setLoading(false);
    }
  });
  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={() => {
        onClose();
        reset();
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle sx={{ p: (theme) => theme.spacing(3, 3, 2, 3), background: '#F4F6F8', mb: 2 }}>
          {isEdit ? 'Update Campaign' : 'Create Campaign'}
          <Iconify
            icon="ri:close-line"
            style={{ position: 'absolute', right: '30px', cursor: 'pointer' }}
            onClick={() => {
              onClose();
              reset();
            }}
          />
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 1, pb: 0, border: 'none' }}>
          <Box
            gap={3}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              lg: 'repeat(2, 1fr)',
            }}
          >
            <RHFTextField name="campaign_name" label={<RequiredLabel text="Campaign Name" />} />
            <RHFSelect
              fullWidth
              name="campaign_type"
              label={<RequiredLabel text="Campaign Type" />}
              PaperPropsSx={{ textTransform: 'capitalize' }}
            >
              {campaign_types.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>
            <Controller
              name="start_date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label={<RequiredLabel text="Start Date" />}
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
            <Controller
              name="end_date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label={<RequiredLabel text="End Date" />}
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {/* <Button
          variant="contained"
          startIcon={<Iconify icon="eva:cloud-upload-fill" />}
          onClick={handleUpload}
        >
          Upload
        </Button> */}
          <Button
            variant="outlined"
            onClick={() => {
              onClose();
              reset();
            }}
            className="danager"
          >
            Cancel
          </Button>
          <LoadingButton loading={loading} variant="contained" onClick={onSubmit}>
            {isEdit ? 'Update' : 'Create'}
          </LoadingButton>

          {/* {!!file.length && (
          <Button variant="outlined" color="inherit" onClick={handleRemoveAllFiles}>
            Remove all
          </Button>
        )} */}
          {/* 
        {(onCreate || onUpdate) && (
          <Stack direction="row" justifyContent="flex-end" flexGrow={1}>
            <Button variant="soft" onClick={onCreate || onUpdate}>
              {onUpdate ? 'Save' : 'Create'}
            </Button>
          </Stack>
        )} */}
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

CreateCampaignModal.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  isEdit: PropTypes.bool,
  item: PropTypes.object,
};
